/**
 * Environment Configuration Utility
 * 
 * This utility provides access to environment variables with the following priority:
 * 1. Runtime environment variables from AWS ECS (via window._env_)
 * 2. Build-time environment variables (via process.env)
 * 3. Default fallback values
 * 
 * Usage:
 *   import { getEnv } from './utils/envConfig';
 *   const region = getEnv('REACT_APP_AWS_REGION', 'us-east-1');
 */

/**
 * Get environment variable value
 * @param {string} key - Environment variable key (e.g., 'REACT_APP_AWS_REGION')
 * @param {string} defaultValue - Default value if not found
 * @returns {string} Environment variable value
 */
export const getEnv = (key, defaultValue = '') => {
  // Priority 1: Runtime environment from AWS ECS (window._env_)
  if (typeof window !== 'undefined' && window._env_ && window._env_[key]) {
    const value = window._env_[key];
    // Check if the value is not a template placeholder (envsubst failed)
    if (value && !value.startsWith('${')) {
      return value;
    }
  }
  
  // Priority 2: Build-time environment (process.env)
  if (process.env[key]) {
    return process.env[key];
  }
  
  // Priority 3: Default value
  return defaultValue;
};

/**
 * Get boolean environment variable
 * @param {string} key - Environment variable key
 * @param {boolean} defaultValue - Default value if not found
 * @returns {boolean} Boolean value
 */
export const getEnvBool = (key, defaultValue = false) => {
  const value = getEnv(key);
  if (!value) return defaultValue;
  return value.toLowerCase() === 'true' || value === '1';
};

/**
 * Check if runtime environment is available
 * @returns {boolean} True if window._env_ is available
 */
export const hasRuntimeEnv = () => {
  return typeof window !== 'undefined' && window._env_ !== undefined;
};

export default {
  getEnv,
  getEnvBool,
  hasRuntimeEnv
};
